/**
 * Currency utility functions for formatting amounts with currency symbols
 */

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  position: 'before' | 'after'; // Symbol position relative to amount
}

export const CURRENCIES: Currency[] = [
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka', position: 'before' },
  { code: 'USD', symbol: '$', name: 'US Dollar', position: 'before' },
  { code: 'EUR', symbol: '€', name: 'Euro', position: 'before' },
  { code: 'GBP', symbol: '£', name: 'British Pound', position: 'before' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', position: 'before' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', position: 'before' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', position: 'before' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', position: 'before' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal', position: 'before' },
];

/**
 * Format amount with currency symbol
 * @param amount - The numeric amount to format
 * @param currencyCode - Currency code (e.g., 'BDT', 'USD')
 * @param currencySymbol - Currency symbol (e.g., '৳', '$')
 * @returns Formatted string with currency symbol
 */
export function formatCurrency(
  amount: number,
  currencyCode: string = 'BDT',
  currencySymbol: string = '৳'
): string {
  const currency = CURRENCIES.find(c => c.code === currencyCode);
  const symbol = currency?.symbol || currencySymbol;
  const position = currency?.position || 'before';
  
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return position === 'before' 
    ? `${symbol}${formattedAmount}`
    : `${formattedAmount}${symbol}`;
}

/**
 * Get currency object by code
 */
export function getCurrencyByCode(code: string): Currency | undefined {
  return CURRENCIES.find(c => c.code === code);
}

/**
 * Get default currency (Bangladeshi Taka)
 */
export function getDefaultCurrency(): Currency {
  return CURRENCIES[0]; // BDT
}
